import type { CatalogItem } from '@viva/core';
import { foldText, tokenize } from '@viva/core';
import type { PlaybackRequest } from '../../state/playback-context';

/** What the HD lookup needs from the catalog. */
export interface UhdFallbackCatalog {
  search(query: { query: string; limit: number; kinds: ['movie'] | ['series']; sourceIds: string[] }): Promise<{ item: CatalogItem }[]>;
  getSeriesDetails(sourceId: string, itemKey: string): Promise<{ seasons: { episodes: CatalogItem[] }[] }>;
}

const UHD_TOKENS = new Set(['4k', 'uhd', '2160p', 'hevc', 'h265', 'x265']);
const QUALITY_TOKENS = new Set([...UHD_TOKENS, 'fhd', 'hd', 'sd', 'lq', 'hq', '1080p', '720p', '576p', '480p', 'hdr', 'hdr10', 'dv']);
/** Quality marks some providers write in small letters ("Lupin ᵁᴴᴰ"). */
const SMALL_MARKS = /ᵁᴴᴰ|ᶠᴴᴰ|ᴴᴰ|ˢᴰ|ᴴᴱⱽᶜ|ᴿᴬᵂ/g;
/** Episode names often repeat the series: "4K - Lupin (2021) (FR) - S01E01 - Chapitre 1". */
const EPISODE_PART = /\s*[-–|:]?\s*\bS\d{1,2}\s*E\d{1,4}\b.*$/i;
/** How many other versions of a series are opened to look for the episode. */
const SERIES_TRIES = 3;

function tokens(name: string): string[] {
  return tokenize(foldText(name.replace(SMALL_MARKS, ' ').replace(/h\.265/gi, 'h265')));
}

/** Whether a name marks a 4K / HEVC version. */
export function isUhdName(name: string): boolean {
  return /ᵁᴴᴰ|ᴴᴱⱽᶜ/.test(name) || tokens(name).some((token) => UHD_TOKENS.has(token));
}

/**
 * The title without quality marks, for comparing versions. Word order doesn't count: providers put the
 * language before or after ("4K - Lupin (2021) (FR)" and "FR - Lupin (2021)" both give "2021 fr lupin").
 */
export function baseTitle(name: string): string {
  return [...new Set(tokens(name).filter((token) => !QUALITY_TOKENS.has(token)))].sort().join(' ');
}

/**
 * The same movie or episode in a version that isn't 4K (HD, LQ…), for devices
 * that can't play 4K HEVC. `null` when the catalog has no other version.
 */
export async function findNonUhdVersion(catalog: UhdFallbackCatalog, request: PlaybackRequest): Promise<PlaybackRequest | null> {
  const { item } = request;
  const { season, episode } = item.data;
  if (season === undefined || episode === undefined) {
    const base = baseTitle(item.name);
    if (!base) return null;
    const hits = await catalog.search({ query: base, limit: 30, kinds: ['movie'], sourceIds: [item.sourceId] });
    const other = hits.map((hit) => hit.item).find((candidate) => candidate.key !== item.key && candidate.url !== null && !isUhdName(candidate.name) && baseTitle(candidate.name) === base);
    return other ? { item: other } : null;
  }
  const seriesName = request.series?.name ?? (EPISODE_PART.test(item.name) ? item.name.replace(EPISODE_PART, '') : '');
  const base = baseTitle(seriesName);
  if (!base) return null;
  const hits = await catalog.search({ query: base, limit: 30, kinds: ['series'], sourceIds: [item.sourceId] });
  const candidates = hits
    .map((hit) => hit.item)
    .filter((candidate) => candidate.key !== request.series?.key && candidate.url === null && !isUhdName(candidate.name) && baseTitle(candidate.name) === base)
    .slice(0, SERIES_TRIES);
  for (const series of candidates) {
    const details = await catalog.getSeriesDetails(series.sourceId, series.key).catch(() => null);
    if (!details) continue;
    const episodes = details.seasons.flatMap((entry) => entry.episodes);
    const index = episodes.findIndex((candidate) => candidate.data.season === season && candidate.data.episode === episode && candidate.url !== null);
    if (index >= 0) return { item: episodes[index]!, series, queue: { items: episodes, index } };
  }
  return null;
}
